// src/pages/LandingPage.jsx
import { Link } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  BookOpen,
  Clock,
  Award,
} from "lucide-react";
import { useAuth } from "@clerk/clerk-react";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_API_BASE?.replace(/\/+$/, "")) ||
  (process.env.REACT_APP_API_BASE &&
    process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  "http://localhost:5050";

export default function LandingPage() {
  const { isSignedIn } = useAuth();

  const [stats, setStats] = useState({ courses: 5, lessons: 109, learners: 500 });
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/content/courses`, { credentials: "include" });
        const isJson = res.headers.get("content-type")?.includes("application/json");
        const json = isJson ? await res.json().catch(() => ({})) : {};
        const list = Array.isArray(json?.data) ? json.data : [];
        const courses = list.length || 5;
        const lessons =
          list.reduce((sum, c) => sum + Number(c.total_lessons || c.lessonCount || 0), 0) || 109;
        if (alive) setStats((s) => ({ ...s, courses, lessons }));
      } catch {
        // keep fallback
      } finally {
        alive && setLoadingStats(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const featureItems = useMemo(
    () => [
      {
        icon: <BookOpen className="w-8 h-8 text-white" />,
        title: "Structured Courses",
        text: "Expertly organized Bible study paths for progressive spiritual growth.",
      },
      {
        icon: <Clock className="w-8 h-8 text-white" />,
        title: "Anytime, Anywhere",
        text: "Self-paced learning that works on mobile and desktop—designed for real life.",
      },
      {
        icon: <Award className="w-8 h-8 text-white" />,
        title: "Track & Certify",
        text: "Progress tracking, assessments, and printable certificates on completion.",
      },
    ],
    []
  );

  const testimonials = [
    {
      quote:
        "I finished Discover and started Daniel & Revelation—this platform keeps me accountable.",
      name: "John K.",
      role: "Member, Nairobi",
    },
    {
      quote:
        "Our district adopted VOP-Virtual School for new members. It’s simple and structured.",
      name: "Ps. Daniel M.",
      role: "District Pastor, Mombasa",
    },
  ];

  const primaryBtn =
    "inline-flex items-center justify-center gap-2 rounded-2xl px-8 py-4 text-lg font-extrabold shadow-xl transition hover:opacity-95";
  const secondaryBtn =
    "inline-flex items-center justify-center rounded-2xl px-8 py-4 text-lg font-bold border border-orange-200 bg-white/80 backdrop-blur hover:bg-white transition";

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-orange-50 via-white to-amber-50">
      {/* Top Auth Bar (no Navbar) */}
      <div className="fixed top-0 inset-x-0 z-40">
        <div className="bg-white/70 backdrop-blur-xl border-b border-orange-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="h-16 flex items-center justify-between">
              {/* Brand */}
              <Link to="/" className="flex items-center gap-2 min-w-0">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 shadow-md" />
                <div className="min-w-0">
                  <div className="text-sm sm:text-base font-extrabold text-slate-900 truncate">
                    Voice Of Prophecy Virtual School
                  </div>
                  <div className="text-[11px] sm:text-xs text-slate-600 truncate">
                    Study • Grow • Serve
                  </div>
                </div>
              </Link>

              {/* Auth Buttons */}
              <div className="flex items-center gap-2">
                {isSignedIn ? (
                  <Link
                    to="/dashboard/learner"
                    className="rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2 font-bold shadow hover:opacity-95 transition"
                  >
                    Go to Dashboard
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className="rounded-xl px-4 py-2 font-bold text-slate-700 hover:text-orange-700 hover:bg-orange-50 transition"
                    >
                      Log in
                    </Link>
                    <Link
                      to="/register"
                      className="rounded-xl bg-gradient-to-r from-orange-600 to-amber-600 text-white px-4 py-2 font-extrabold shadow hover:opacity-95 transition"
                    >
                      Sign up
                    </Link>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -left-10 w-72 h-72 bg-orange-300 rounded-full blur-3xl opacity-25 animate-blob" />
          <div className="absolute top-10 right-0 w-80 h-80 bg-amber-300 rounded-full blur-3xl opacity-25 animate-blob animation-delay-2000" />
          <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-25 animate-blob animation-delay-4000" />
        </div>

        <div className="relative pt-28 pb-14 px-4 sm:px-6 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur border border-orange-200/60 text-orange-700 px-5 py-2.5 rounded-full text-sm font-extrabold mb-6 shadow-sm">
            <Sparkles className="w-4 h-4" />
            Trusted Bible Study Platform Across Kenya
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black leading-tight tracking-tight text-slate-900">
            Study the Word.
            <br />
            <span className="bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">
              Grow in Faith.
            </span>
          </h1>

          <p className="text-lg sm:text-xl md:text-2xl text-slate-600 max-w-3xl mx-auto mt-5 leading-relaxed font-medium">
            Structured Bible courses, interactive lessons, and assessments—designed for individuals,
            families, and churches.
          </p>

          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            {isSignedIn ? (
              <>
                <Link
                  to="/dashboard/learner"
                  className={`${primaryBtn} bg-gradient-to-r from-orange-600 to-amber-600 text-white`}
                >
                  Go to your Dashboard <span aria-hidden>→</span>
                </Link>
                <a href="#features" className={secondaryBtn}>
                  Explore Features
                </a>
              </>
            ) : (
              <>
                <Link
                  to="/register"
                  className={`${primaryBtn} bg-gradient-to-r from-orange-600 to-amber-600 text-white`}
                >
                  Get Started Free <span aria-hidden>→</span>
                </Link>
                <a href="#features" className={secondaryBtn}>
                  Learn More
                </a>
              </>
            )}
          </div>

          {/* Stats */}
          <div className="relative mt-12">
            <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-orange-500/10 to-amber-500/10 blur-xl" />
            <div className="relative bg-white/80 backdrop-blur-sm border border-orange-100 rounded-3xl shadow-xl p-6 md:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <StatBlock loading={loadingStats} value={stats.learners + "+"} label="Active Learners" />
                <StatBlock loading={loadingStats} value={String(stats.courses)} label="Bible Courses" />
                <StatBlock loading={loadingStats} value={String(stats.lessons)} label="Lessons" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Features */}
      <section id="features" className="relative py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-3xl md:text-4xl font-black tracking-tight text-slate-900">
              Built for Individual Growth & Church Discipleship
            </h2>
            <p className="text-slate-600 mt-3 max-w-3xl mx-auto">
              Enroll in Discover, Ugunduzi (Swahili), Daniel & Revelation, Health, and Family—with
              assessments and certificates on completion.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8">
            {featureItems.map((f, i) => (
              <article
                key={i}
                className="group bg-white rounded-2xl border border-orange-100 p-6 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl blur-lg opacity-35 group-hover:opacity-60 transition" />
                  <div className="relative bg-gradient-to-r from-orange-600 to-amber-600 w-16 h-16 rounded-2xl mx-auto flex items-center justify-center group-hover:scale-110 transition">
                    {f.icon}
                  </div>
                </div>
                <h3 className="text-xl font-extrabold mb-2 text-slate-900">{f.title}</h3>
                <p className="text-slate-600">{f.text}</p>
              </article>
            ))}
          </div>

          {/* Quick course strip */}
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { id: "discover", title: "Discover", desc: "26 lessons • Foundations of faith" },
              { id: "ugunduzi", title: "Ugunduzi (Swahili)", desc: "26 lessons • Safari ya Biblia" },
              { id: "daniel-revelation", title: "Daniel & Revelation", desc: "21 lessons • Prophetic study" },
              { id: "focus-family", title: "Focus on the Family", desc: "16 lessons • Family life" },
            ].map((c) => (
              <Link
                to={`/courses/${c.id}`}
                key={c.id}
                className="group bg-white rounded-2xl border border-orange-100 p-5 shadow-lg hover:shadow-2xl hover:-translate-y-1 transition"
              >
                <div className="flex items-start justify-between">
                  <div className="bg-gradient-to-r from-orange-600 to-amber-600 w-12 h-12 rounded-xl flex items-center justify-center shadow">
                    <BookOpen className="text-white" />
                  </div>
                  <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-1 rounded-full">
                    Available
                  </span>
                </div>
                <h4 className="mt-4 text-lg font-extrabold text-slate-900 group-hover:text-orange-700 transition-colors">
                  {c.title}
                </h4>
                <p className="text-slate-600 text-sm mt-1">{c.desc}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-orange-600">
                  View course <span aria-hidden>→</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900">
              What learners are saying
            </h2>
            <p className="text-slate-600 mt-2">Real stories from across Kenya.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((t, i) => (
              <figure
                key={i}
                className="bg-white rounded-2xl border border-orange-100 p-6 shadow-lg"
              >
                <p className="text-slate-700 italic leading-relaxed">“{t.quote}”</p>
                <figcaption className="mt-4">
                  <div className="font-semibold text-slate-900">{t.name}</div>
                  <div className="text-sm text-slate-500">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="px-4 sm:px-6 pb-12 sm:pb-16">
        <div className="max-w-6xl mx-auto bg-gradient-to-r from-orange-600 to-amber-600 rounded-3xl text-white p-8 sm:p-10 shadow-2xl overflow-hidden relative">
          <div className="absolute right-[-40px] bottom-[-40px] w-64 h-64 rounded-full bg-white/10 blur-2xl pointer-events-none" />
          <h3 className="text-2xl md:text-3xl font-black">
            Ready to begin your next study?
          </h3>
          <p className="text-orange-50 mt-1">
            Enroll now and continue at your own pace. Your progress is saved automatically.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={isSignedIn ? "/dashboard/learner" : "/register"}
              className="inline-flex items-center justify-center rounded-xl bg-white text-slate-900 px-6 py-3 font-extrabold shadow hover:opacity-95 transition"
            >
              {isSignedIn ? "Go to Dashboard" : "Create Free Account"}
            </Link>
            <Link
              to="/courses"
              className="inline-flex items-center justify-center rounded-xl border border-white/30 bg-white/10 px-6 py-3 font-bold hover:bg-white/15 transition"
            >
              Browse Courses
            </Link>
          </div>
        </div>
      </section>

      {/* Small Footer (Option A) */}
      <footer className="mt-auto border-t border-orange-100 bg-white/70 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-slate-600">
            <div className="font-semibold text-slate-700">
              © {new Date().getFullYear()} Voice Of Prophecy Virtual School · Powered by{" "}
              <span className="text-orange-700 font-extrabold">VOP</span>
            </div>

            <div className="flex items-center gap-4">
              <a href="#" className="hover:text-orange-700 transition-colors font-semibold">
                Terms
              </a>
              <a href="#" className="hover:text-orange-700 transition-colors font-semibold">
                Privacy
              </a>
              <Link to="/feedback" className="hover:text-orange-700 transition-colors font-semibold">
                Contact
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function StatBlock({ value, label, loading }) {
  return (
    <div className="text-center">
      <div className="text-4xl md:text-5xl font-black bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
        {loading ? "—" : value}
      </div>
      <div className="text-slate-600 font-semibold mt-1">{label}</div>
    </div>
  );
}
